import React from "react";
import i18n from "../../../../i18n/i18n";
import { AxiosResponse } from "axios";
import { HttpErrorModel } from "../../../../common/models/httpError";
import { RouteComponentProps, withRouter } from "react-router";
import { Api } from "../../../../helpers/api";
import { HttpError } from "../../../../common/components/shared/httpError/httpError";
import Loader from "../../../../common/components/shared/loader/loader";
import { RouteParamsModel } from "../../../../common/models/routeParams";
import { ManifestDetailModel } from "../../../models/manifest";
import { ManifestService } from "../../../services/manifestService";
import SimpleInfoElement from "../../../../common/components/shared/simpleInfoElement/simpleInfoElement";
import Row from "../../../../common/components/shared/row/row";
import { getContainerSize } from "../../../../helpers/containerSize";
import { LoadingTypeFirstLetter } from "../../../models/enums";
import { Utils } from "../../../../helpers/utils";
import ReloadButton from "../../../../common/components/shared/reloadButton/reloadButton";

interface ManifestDetailsState {
    manifestData?: ManifestDetailModel | null;
    response: AxiosResponse<HttpErrorModel[] | any> | undefined;
    manifestId: string | null;
}

class ManifestDetails extends React.Component<RouteComponentProps,ManifestDetailsState> {
    constructor(props: any) {
        super(props);

        this.state = {
            manifestData: null,
            response: undefined,
            manifestId: null
        };
    }

    async componentDidMount() {
        const params = this.props.match.params as RouteParamsModel;
        const response = await ManifestService.getManifest(params.id);

        this.setState({
            manifestData: Api.isSuccess(response) ? response?.data : null,
            response,
            manifestId: params.id
        });
    }

    public render(): JSX.Element {
        return (
            <div className="manifests-details">
                <div className="content manifest-content">
                    {this.renderContent()}
                </div>
            </div>
        );
    }

    private renderContent() {
        if (!Api.isSuccess(this.state.response)) {
            return (
                <HttpError
                    response={this.state.response}
                    cause={i18n.t(
                        "documents.manifests.details.retrievalErrorSuffix"
                    )}
                />
            );
        } else if (this.state.manifestData) {
            return this.renderDetails();
        } else {
            return <Loader />;
        }
    }

    private renderDetails() {
        return (
            <>
                {this.renderHeader()}
                <Row class="flex-type">
                    <SimpleInfoElement
                        label={i18n.t("documents.manifests.details.properties.ferryName")}
                        value={this.state.manifestData!.ferry?.name}
                        testId="ferry-name"
                    />
                    <SimpleInfoElement
                        label={i18n.t("documents.manifests.details.properties.ferryImoCode")}
                        value={this.state.manifestData!.ferry?.imoCode}
                        testId="ferry-imo-code"
                    />
                    <SimpleInfoElement
                        label={i18n.t("documents.manifests.details.properties.shipownerImoCode")}
                        value={this.state.manifestData!.shipownerImoCode}
                        testId="shipowner-imo-code"
                    />
                    <SimpleInfoElement
                        label={i18n.t("documents.manifests.details.properties.voyageNo")}
                        value={this.state.manifestData!.voyageNo}
                        testId="voyage-no"
                    />
                    <SimpleInfoElement
                        label={i18n.t("documents.manifests.details.properties.arrivalDepartureTime")}
                        value={Utils.renderTime(this.state.manifestData!.arrivalDepartureTime)}
                        testId="arrival-departure-time"
                    />
                </Row>
                <Row class="flex-type">
                    <SimpleInfoElement
                        label={i18n.t("documents.manifests.details.properties.bookingNo")}
                        value={this.state.manifestData!.bookingNo}
                        testId="booking-no"
                    />
                    <SimpleInfoElement
                        label={i18n.t("documents.manifests.details.properties.loadingType")}
                        value={LoadingTypeFirstLetter[this.state.manifestData!.loadingType]}
                        testId="loading-type"
                    />
                    <SimpleInfoElement
                        label={i18n.t("documents.manifests.details.properties.lorryRegNo")}
                        value={this.state.manifestData!.lorryRegNo}
                        testId="lorry-reg-no"
                    />
                    <SimpleInfoElement
                        label={i18n.t("documents.manifests.details.properties.trailerRegNo")}
                        value={this.state.manifestData!.trailerRegNo}
                        testId="trailer-reg-no"
                    />
                    <SimpleInfoElement
                        label={i18n.t("documents.manifests.details.properties.length")}
                        value={this.state.manifestData!.length}
                        testId="length"
                    />
                </Row>
                <Row class="flex-type">
                    <SimpleInfoElement
                        label={i18n.t("documents.manifests.details.properties.cargoDesc")}
                        value={this.state.manifestData!.cargoDesc}
                        testId="cargo-desc"
                    />
                    <SimpleInfoElement
                        label={i18n.t("documents.manifests.details.properties.weight")}
                        value={this.state.manifestData!.weight}
                        unit="kg"
                        testId="weight"
                    />
                    <SimpleInfoElement
                        label={i18n.t("documents.manifests.details.properties.plugin")}
                        value={Utils.renderBooleanYesNoValue(this.state.manifestData!.plugin, true)}
                        testId="plugin"
                    />
                    <SimpleInfoElement
                        label={i18n.t("documents.manifests.details.properties.imdgClass")}
                        value={this.state.manifestData!.imdgClass}
                        testId="imdg-class"
                    />
                    <SimpleInfoElement
                        label={i18n.t("documents.manifests.details.properties.payableBy")}
                        value={this.state.manifestData!.payableBy}
                        testId="payable-by"
                    />
                </Row>
                <Row class="flex-type">
                    <SimpleInfoElement
                        label={i18n.t("documents.manifests.details.properties.shipperName")}
                        value={this.state.manifestData!.shipperName}
                        testId="shipper-name"
                    />
                    <SimpleInfoElement
                        label={i18n.t("documents.manifests.details.properties.originCountry")}
                        value={this.state.manifestData!.originCountry}
                        testId="origin-country"
                    />
                    <SimpleInfoElement
                        label={i18n.t("documents.manifests.details.properties.destinationCountry")}
                        value={this.state.manifestData!.destinationCountry}
                        testId="destination-country"
                    />
                    <SimpleInfoElement
                        label={i18n.t("documents.manifests.details.properties.overwide")}
                        value={Utils.renderBooleanYesNoValue(this.state.manifestData!.overwide, true)}
                        testId="overwide"
                    />
                    <SimpleInfoElement
                        label={i18n.t("documents.manifests.details.properties.freight")}
                        value={this.state.manifestData!.freight}
                        testId="freight"
                    />
                </Row>
                <Row class="flex-type">
                    <SimpleInfoElement
                        label={i18n.t("documents.manifests.details.properties.currency")}
                        value={this.state.manifestData!.currency}
                        testId="currency"
                    />
                    <SimpleInfoElement
                        label={i18n.t("documents.manifests.details.properties.containerNo")}
                        value={this.state.manifestData!.containerNo}
                        testId="container-no"
                    />
                    <SimpleInfoElement
                        label={i18n.t("documents.manifests.details.properties.containerSize")}
                        value={getContainerSize(this.state.manifestData!.containerSize)}
                        testId="container-size"
                    />
                    <SimpleInfoElement
                        label={i18n.t("documents.manifests.details.properties.vgm")}
                        value={this.state.manifestData!.vgm}
                        unit="kg"
                        testId="vgm"
                    />
                    <SimpleInfoElement
                        label={i18n.t("documents.manifests.details.properties.billOfLading")}
                        value={Utils.renderBooleanYesNoValue(this.state.manifestData!.billOfLading, true)}
                        testId="bill-of-lading"
                    />
                </Row>
            </>
        );
    }

    private renderHeader() {
        return (
            <div className="header" data-testid="header">
                <span>
                    {i18n.t("documents.manifests.details.header", {
                        number: this.state.manifestData!.id
                    })}
                </span>
                <ReloadButton
                    reloadFunc={this.reload.bind(this)}
                    testId="manifest-details-reload-btn"
                />
            </div>
        );
    }

    private async reload() {
        this.setState({manifestData: undefined});
        const response = this.state.manifestId ? await ManifestService.getManifest(this.state.manifestId!) : undefined;

        this.setState({
            manifestData: Api.isSuccess(response) ? response?.data : null,
            response
        });
    }
}

export default withRouter(ManifestDetails);
